import { useCallback, useState } from "react";
import { getSupabase } from "../lib/supabase";

export interface EvidenceHit {
  chunk_id: string;
  evidence_id: string;
  source_label: string;
  occurred_on: string | null;
  snippet: string;
  score: number;
}

export interface AskResult {
  mode: "ask" | "find";
  query: string;
  answer: string | null;
  found: boolean;
  citations: EvidenceHit[];
  hits: EvidenceHit[];
}

/**
 * The Hell Hounds' evidence chat. Sends the question to the retriever-query
 * edge function: hybrid semantic retrieval (vector + full-text + neighbor
 * expansion) over the evidence corpus, plus a grounded answer that may cite
 * only the excerpts it was shown — citations resolve to real chunks.
 */
export function useEvidenceAsk(caseId: string | undefined) {
  const [result, setResult] = useState<AskResult | null>(null);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = useCallback(async (query: string, mode: "ask" | "find" = "ask") => {
    if (!caseId || asking) return;
    const sb = getSupabase();
    if (!sb) { setError("Supabase not configured"); return; }
    setError(null);
    setAsking(true);
    setResult(null);
    try {
      const { data, error: e } = await sb.functions.invoke("retriever-query", {
        body: { case_id: caseId, query, mode },
      });
      if (e) throw new Error(e.message);
      if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
      setResult(data as AskResult);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAsking(false);
    }
  }, [caseId, asking]);

  const reset = useCallback(() => { setResult(null); setError(null); }, []);

  return { ask, reset, result, asking, error };
}
